import {
    Backdrop,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface ConfirmationDialogProps {
    open: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    severity?: 'error' | 'warning' | 'info';
    onConfirm: () => void;
    onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    severity = 'warning',
    onConfirm,
    onCancel,
}) => {
    const theme = useTheme();

    const severityColors = {
        error: theme.palette.error,
        warning: theme.palette.warning,
        info: theme.palette.info,
    };

    const color = severityColors[severity];

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            slots={{
                backdrop: (props) => <Backdrop
                    data-testid="dialog-backdrop"
                    {...props}
                />
            }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minWidth: 320,
                    bgcolor: theme.palette.background.paper,
                    boxShadow: `0 0 24px ${color.main}22`,
                    border: `1px solid ${color.main}33`,
                }
            }}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningIcon sx={{ color: color.main }} />
                    <Typography variant="h6" component="span">
                        {title}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'text.primary' }}>
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onCancel}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={severity}
                    data-severity={severity}
                    sx={{
                        minWidth: 100,
                        bgcolor: color.main,
                        '&:hover': {
                            bgcolor: color.dark,
                        }
                    }}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog; 